'use client';

import React from 'react';

import {MediaQuery} from '@constants/theme';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiButton from '@new-components/atoms/MuiButton';
import {FormCheckboxInput} from '@new-components/molecules/FormCheckboxInput';
import {FormEmailInput} from '@new-components/molecules/FormEmailInput';
import {FormMobileInput} from '@new-components/molecules/FormMobileInput';
import {FormTextInput} from '@new-components/molecules/FormTextInput';
import {FormHelperText} from '@mui/material';
import {
  validationSchema,
  InputNames,
  parseDataForSubmit,
  parseValidationErrors,
  GA_DARNA_METADATA,
} from './FormConfig';
import {getValueStrFromCountyCodeAndValue} from '@services/new/common/getLeadMeta';
import {useUpdateRecaptcha} from '@damascus-components/Recaptcha/Recaptcha';
import {hasCaptchaSiteKey} from '@utils/getCaptchaSiteKey';
import {useLeadSourceParams} from '@hooks/useLeadSourceParams';
import {getFormDataCache, setFormDataCache} from '@utils/formDataCache';
import {FormTypes} from '../LeadForm/FormConfig';
import {trackFormEvents} from '@utils/googleAnalytics/googleAnalyticsEvents';
import {logEvent} from '@utils/googleAnalytics';

type StateType = {
  values: {
    country: string;
    firstName: string;
    lastName: string;
    email: string;
    contact: string;
    countryCode: string;
  };
};
type ActionType =
  | {type: 'SET_DATA_CACHE'; payload: string}
  | {type: 'SET_COUNTRY'; payload: string};

const INITIAL_STATE: StateType = {
  values: {
    country: getValueStrFromCountyCodeAndValue('+971', 'United Arab Emirates'),
    firstName: '',
    lastName: '',
    email: '',
    contact: '',
    countryCode: '',
  },
};
function Reducer(state: StateType, action: ActionType): StateType {
  switch (action.type) {
    case 'SET_COUNTRY': {
      return {
        values: {...state.values, country: action.payload},
      };
    }

    case 'SET_DATA_CACHE': {
      const overallCacheData = getFormDataCache(INITIAL_STATE.values);
      if (overallCacheData === INITIAL_STATE.values) return state;
      const {country, countryCode, ...savedData} = overallCacheData;
      return {
        values: {
          ...state.values,
          ...savedData,
          country: countryCode,
        },
      };
    }
    default:
      return state;
  }
}

export type LeadFormProps = {
  countryCodeOptions: Record<
    'label' | 'value' | 'flagCode' | 'code' | 'name' | 'displayLabel',
    string
  >[];
  onSubmit: (data: any) => void;
  loading?: boolean;
  formType: FormTypes;
};
export function DarnaForm(props: LeadFormProps) {
  const [state, dispatch] = React.useReducer(Reducer, {
    ...INITIAL_STATE,
  });
  const [errors, setErrors] = React.useState<
    Partial<Record<InputNames, string>>
  >({});
  const {getRecaptchaToken} = useUpdateRecaptcha();

  React.useEffect(() => {
    dispatch({type: 'SET_DATA_CACHE', payload: ''});
  }, []);

  const dispatchAction =
    <T extends ActionType['type']>(type: T) =>
    (payload: Extract<ActionType, {type: T}>['payload']) =>
      dispatch({type, payload} as ActionType);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = Object.fromEntries(formData) as Record<InputNames, string>;
    try {
      await validationSchema?.validate(data, {
        abortEarly: false,
      });
      setErrors({});
      setFormDataCache(data);
      const response = parseDataForSubmit(data);

      const responseKey = await getRecaptchaToken();
      if (hasCaptchaSiteKey() && !responseKey) {
        setErrors({consent: 'Please refresh the page and try again.'});
        return;
      }

      props.onSubmit({...response, responseKey});
    } catch (validationError: any) {
      setErrors(parseValidationErrors(validationError));
    }
  };

  const onBlur = () => {
    trackFormEvents('start');
    logEvent({
      event: 'form_start',
      additionalInfo: {
        form_id: GA_DARNA_METADATA.formId,
        form_type: GA_DARNA_METADATA.formType,
      },
    });
  };

  return (
    <MuiBox
      component='form'
      onBlur={onBlur}
      onSubmit={onSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}>
      <MuiBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          [MediaQuery.desktop]: {
            flexDirection: 'row',
            gap: '18px',
          },
        }}>
        <FormTextInput
          id='firstname'
          label='First Name'
          name='firstName'
          error={!!errors.firstName}
          helperText={errors.firstName}
          disabled={props.loading}
          defaultValue={state.values.firstName}
        />
        <FormTextInput
          id='lastname'
          label='Last Name'
          name='lastName'
          error={!!errors.lastName}
          helperText={errors.lastName}
          disabled={props.loading}
          defaultValue={state.values.lastName}
        />
      </MuiBox>
      <FormMobileInput
        id='contact'
        label='Mobile Number'
        name='contact'
        error={!!errors.contact}
        helperText={errors.contact}
        disabled={props.loading}
        defaultValue={state.values.contact}
        countrySelectProps={{
          options: props.countryCodeOptions,
          name: 'countryCode',
          value: state.values.country,
          onChange: dispatchAction('SET_COUNTRY'),
          disabled: props.loading,
        }}
      />
      <FormEmailInput
        id='email'
        label='Email'
        name='email'
        error={!!errors.email}
        helperText={errors.email}
        disabled={props.loading}
        defaultValue={state.values.email}
      />
      <FormCheckboxInput
        id='consent'
        name='consent'
        label={
          <>
            I consent to the{' '}
            <a
              href='https://www.aldar.com/en/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'>
              Privacy Policy
            </a>{' '}
            &
            <a
              href='https://www.aldar.com/en/terms-and-conditions'
              target='_blank'
              rel='noopener noreferrer'>
              Terms and conditions
            </a>
          </>
        }
        disabled={props.loading}
      />
      <FormHelperText sx={{minHeight: '20px'}} error>
        {errors['consent']}
      </FormHelperText>
      <MuiButton
        type='submit'
        name='action'
        disabled={props.loading}
        sx={{
          marginTop: '12px',
          border: '1px solid #EB6924',
          boxShadow: '0px 4px 20px 0px #EB69244D',
          backgroundColor: '#EB6924',

          color: '#ffffff',
          fontSize: '17px',
          fontWeight: 400,
          lineHeight: '23.8px',
          letterSpacing: ' -0.4000000059604645px',
          textAlign: 'center',
          textUnderlinePosition: 'from-font',
          textDecorationSkipInk: 'none',
          ['&:hover']: {
            border: '1px solid #EB6924',
            boxShadow: '0px 4px 20px 0px #EB69244D',
            backgroundColor: '#EB6924',
          },
        }}>
        {props.loading ? 'Loading...' : 'Submit'}
      </MuiButton>
    </MuiBox>
  );
}
